/**
 * StormRunSkillEffect — Blades tree slash path: sprint-through line attack.
 *
 * Conditional active skill: requires 2+ Blade Dance stacks to activate.
 * The player sprints through up to 3 enemies in a line, dealing Flurry
 * damage to each contact. Stacks are not consumed — each contact counts
 * as a blade hit and continues feeding Blade Dance.
 *
 * Supports upgrade keys via resolver:
 * - Through and Through: `damagePerTargetBonus` (additive, per target after first)
 * - Blade Trail: `trailDurationMs` + `trailDamageMultiplier` (zone spawning)
 *
 * Implements SkillEffect directly with own cooldown management.
 * Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see blades-spec.md § Storm Run
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface StormRunDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly combat: {
    /** Fire a line attack, returns array of entity IDs hit (in order). */
    startLineAttack(attackType: string, maxTargets: number): string[];
  };
  readonly bladeDance: {
    /** Current Blade Dance stack count. */
    readonly stacks: number;
  };
  readonly zones: {
    /** Spawn a trail zone along the run path. */
    create(config: {
      type: string;
      durationMs: number;
      entryDamage: number;
      oneShot: boolean;
      sourceSkillId: string;
    }): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface StormRunConfig {
  readonly cooldownMs: number;
  readonly baseDamageMultiplier: number;
  readonly maxTargets: number;
  readonly requiredBladeDanceStacks: number;
}

const DEFAULT_CONFIG: StormRunConfig = {
  cooldownMs: 2000,                       // long cooldown tier
  baseDamageMultiplier: 1.0,              // Flurry base damage per hit
  maxTargets: 3,                          // sprint through up to 3 enemies
  requiredBladeDanceStacks: 2,            // activation gate
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class StormRunSkillEffect implements SkillEffect {
  readonly skillId = 'storm_run';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: StormRunDeps;
  private readonly config: StormRunConfig;

  private _cooldownRemaining = 0;
  private _activationCount = 0;
  private _totalEnemiesHit = 0;
  private _lastRunTargets: string[] = [];
  private _resolvedDamageMultiplier = 1.0;
  private _resolvedDamagePerTargetBonus = 0;
  private _trailSpawned = false;
  private _resolvedTrailDurationMs = 0;
  private _resolvedTrailDamageMultiplier = 0;

  constructor(deps: StormRunDeps, config: Partial<StormRunConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activationCount(): number { return this._activationCount; }
  get totalEnemiesHit(): number { return this._totalEnemiesHit; }
  get lastRunTargets(): string[] { return this._lastRunTargets; }
  get resolvedDamageMultiplier(): number { return this._resolvedDamageMultiplier; }
  get resolvedDamagePerTargetBonus(): number { return this._resolvedDamagePerTargetBonus; }
  get trailSpawned(): boolean { return this._trailSpawned; }
  get resolvedTrailDurationMs(): number { return this._resolvedTrailDurationMs; }
  get resolvedTrailDamageMultiplier(): number { return this._resolvedTrailDamageMultiplier; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0 &&
      this.deps.bladeDance.stacks >= this.config.requiredBladeDanceStacks;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve damage multiplier
    this._resolvedDamageMultiplier = this.config.baseDamageMultiplier *
      this.deps.resolver.getMultiplicativeEffect('storm_run', 'damage');

    // Resolve Through and Through bonus
    this._resolvedDamagePerTargetBonus =
      this.deps.resolver.getAdditiveEffect('storm_run', 'damagePerTargetBonus');

    // Fire the line attack
    this._lastRunTargets = this.deps.combat.startLineAttack(
      'storm_run',
      this.config.maxTargets,
    );
    this._totalEnemiesHit += this._lastRunTargets.length;
    this._activationCount++;

    // Resolve and spawn Blade Trail (if upgrade unlocked)
    this._resolvedTrailDurationMs =
      this.deps.resolver.getAdditiveEffect('storm_run', 'trailDurationMs');
    this._resolvedTrailDamageMultiplier =
      this.deps.resolver.getAdditiveEffect('storm_run', 'trailDamageMultiplier');

    if (this._resolvedTrailDurationMs > 0 && this._resolvedTrailDamageMultiplier > 0) {
      this.deps.zones.create({
        type: 'blade_light_trail',
        durationMs: this._resolvedTrailDurationMs,
        entryDamage: this._resolvedDamageMultiplier * this._resolvedTrailDamageMultiplier,
        oneShot: true,
        sourceSkillId: 'storm_run',
      });
      this._trailSpawned = true;
    } else {
      this._trailSpawned = false;
    }

    // Start cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  /**
   * Calculate the damage multiplier for a specific target position.
   * Position 0 = first target (1.0×), position 1 = second (1.0 + bonus), etc.
   */
  getDamageForPosition(position: number): number {
    return this._resolvedDamageMultiplier * (1 + position * this._resolvedDamagePerTargetBonus);
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false; // instant skill
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
